import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { cn } from "../../lib/utils.js";
import "./shared/quiz-blank.js";
import { KatexText } from "./shared/katex-text.js";
import { useTranslation } from "react-i18next";
import "./shared/quiz-style-context.js";
import { SubmitButton } from "./shared/submit-button.js";
import "../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "./shared/quiz-media.js";
import "../../context/DevToolsContext.js";
import "../../utils/quiz-parser.js";
import { parseSelectTextOptionModels } from "./hooks/use-select-text-quiz.js";
import { motion } from "../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function SelectTextQuizForDiary({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false
}) {
  const { t } = useTranslation("quiz");
  const [startTime] = useState(() => Date.now());
  const isSolved = solveResult !== null && solveResult !== void 0;
  const options = useMemo(() => {
    const reg = /\{\{(.*?)}}/im;
    const match = reg.exec(quiz.problem);
    if (!match) return [];
    return parseSelectTextOptionModels(match[1]).filter((option) => option.isSelectable);
  }, [quiz.problem]);
  const computedInitialAnswer = (initialAnswersProp == null ? void 0 : initialAnswersProp[0]) ?? "";
  const [selectedRawIndex, setSelectedRawIndex] = useState(
    computedInitialAnswer !== "" ? parseInt(computedInitialAnswer) : null
  );
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (computedInitialAnswer !== "") {
      setSelectedRawIndex(parseInt(computedInitialAnswer));
    } else {
      setSelectedRawIndex(null);
    }
  }, [computedInitialAnswer]);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (hideSubmitButton && selectedRawIndex !== null && !isSolved && !isViewSolved) {
      onSolve == null ? void 0 : onSolve(quizId, [selectedRawIndex.toString()], quiz.vars, Date.now() - startTime);
    }
  }, [selectedRawIndex, hideSubmitButton, isSolved, isViewSolved, onSolve, quizId, quiz.vars, startTime]);
  const canSubmit = selectedRawIndex !== null;
  const handleSelect = useCallback((rawIndex) => {
    if (isSolved || isViewSolved) return;
    setSelectedRawIndex(rawIndex);
  }, [isSolved, isViewSolved]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved || selectedRawIndex === null) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, [selectedRawIndex.toString()], quiz.vars, elapsedTime);
  }, [selectedRawIndex, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-3",
        children: [
          /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1.5 text-sm font-bold text-white bg-linear-to-r from-blue-500 to-cyan-500 rounded-full shadow-2xs", children: /* @__PURE__ */ jsx("span", { children: t("types.newSelectText.label") }) }),
          /* @__PURE__ */ jsx("h3", { className: "text-xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        role: "radiogroup",
        "aria-label": t("types.newSelectText.selectOption"),
        className: "flex flex-wrap gap-2",
        children: options.map((option, visibleIndex) => {
          const isSelected = selectedRawIndex === option.rawIndex;
          return /* @__PURE__ */ jsxs(
            motion.button,
            {
              role: "radio",
              "aria-checked": isSelected,
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              transition: { delay: visibleIndex * 0.03 },
              onClick: () => handleSelect(option.rawIndex),
              disabled: isSolved || isViewSolved,
              className: cn(
                "px-3 py-1.5 rounded-lg transition-all text-base font-medium",
                // 기본 상태
                !isSelected && "bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700",
                // 선택 상태
                isSelected && "bg-blue-500 text-white shadow-2xs",
                // 제출 후 비활성화
                (isSolved || isViewSolved) && "cursor-not-allowed opacity-70"
              ),
              children: [
                /* @__PURE__ */ jsx("span", { className: "font-bold", children: "#" }),
                /* @__PURE__ */ jsx(KatexText, { text: option.label })
              ]
            },
            `option-${option.rawIndex}`
          );
        })
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel ?? t("types.newSelectText.submit")
      }
    ),
    isSolved && /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-gray-600 bg-gray-100 rounded-xl", children: t("types.newSelectText.submitted") })
  ] });
}
export {
  SelectTextQuizForDiary
};
//# sourceMappingURL=new-select-text-quiz.js.map
